<template>
    <v-window v-model="step">
        <v-window-item value="default">
            <v-row no-gutters class="my-2">
                <v-col class="d-flex align-center flex-shrink-0 flex-grow-1">
                    <v-text-field-filled
                        v-model="search"
                        label="Rechercher"
                        hide-details
                        density="compact"
                        clearable
                        append-inner-icon="$search"
                        @click:clear="search = ''"
                    />
                </v-col>
                <v-col class="d-flex align-center justify-end flex-shrink-1 flex-grow-0 pl-2">
                    <v-btn
                        @click="
                            step = 'createUser';
                            emitStepFromComponent();
                        "
                        rounded="xl"
                        elevation="0"
                        size="small"
                        data-cy="ThirdCompanyUsersEdition-add"
                    >
                        Ajouter <v-icon right>$add</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
            <v-list
                subheader
                style="height: 40vh; overflow-y: auto"
                v-if="!loading && thirdPartyCompanyData"
            >
                <template v-if="!thirdPartyCompanyUsers.length">
                    Aucun resultat trouvé pour la recherche
                </template>
                <v-list-item
                    v-for="(client, i) in thirdPartyCompanyUsers"
                    :key="i"
                    @click="selectClient(client)"
                    :class="getClientClass(client.email)"
                    style="background: #fff"
                    :data-cy="'selectInvoiceProduct-product-' + i"
                    lines="two"
                    rounded="xl"
                    border
                >
                    <v-list-item-title>
                        {{ client.firstname }} {{ client.lastname }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                        >E-mail: {{ client.email }} Téléphone: {{ client.phoneNumber }}
                    </v-list-item-subtitle>
                    <template #append class="my-0">
                        <v-btn-icon
                            v-if="!showEditUsers"
                            :icon="
                                client.email === selectedClient.email
                                    ? '$radio_button_checked'
                                    : '$radio_button_unchecked'
                            "
                        >
                        </v-btn-icon>
                        <v-menu v-if="showEditUsers" offset-y>
                            <template v-slot:activator="{ props }">
                                <v-btn-icon icon="$dots_vertical" v-bind="props" />
                            </template>
                            <v-list>
                                <v-list-item @click="editUser(client)">
                                    <v-list-item-title>Modifier</v-list-item-title>
                                    <template #append>
                                        <v-icon size="small">$oss-edit</v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item @click="deleteUser(client)">
                                    <v-list-item-title>Supprimer</v-list-item-title>
                                    <template #append>
                                        <v-icon size="small">$oss-trash</v-icon>
                                    </template>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-list-item>
            </v-list>
            <v-row v-else-if="!loading">
                <v-col cols="12" class="d-flex align-center justify-center">
                    Vous n'avez aucun client enregistré
                </v-col>
                <v-col cols="12" class="d-flex align-center justify-center">
                    <v-btn
                        @click="
                            step = 'createUser';
                            emitStepFromComponent();
                        "
                        rounded="xl"
                        elevation="0"
                        dark
                        size="small"
                    >
                        Ajouter <v-icon right>$add</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
            <v-row v-else>
                <v-col cols="12" class="text-center">
                    <v-progress-circular indeterminate color="primary" />
                </v-col>
            </v-row>
        </v-window-item>
        <v-window-item value="createUser">
            <v-form v-model="newUserValid">
                <v-row no-gutters class="mt-2">
                    <v-col cols="6" class="pr-1">
                        <v-text-field-filled
                            v-model="newUser.firstname"
                            label="Prénom"
                            hide-details
                            :rules="[(v) => !!v || $t('settings.itemRequired')]"
                            data-cy="ThirdCompanyUsersEdition-firstName"
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="6" class="pl-1">
                        <v-text-field-filled
                            v-model="newUser.lastname"
                            label="Nom"
                            hide-details
                            :rules="[(v) => !!v || $t('settings.itemRequired')]"
                            data-cy="ThirdCompanyUsersEdition-lastName"
                            density="compact"
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters class="mt-2">
                    <v-col cols="6" class="pr-1">
                        <v-text-field-filled
                            v-model="newUser.email"
                            label="Adresse mail"
                            :rules="[rules.isEmail]"
                            data-cy="ThirdCompanyUsersEdition-mail"
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="6" class="pr-1">
                        <v-text-field-filled
                            v-model="newUser.phoneNumber"
                            label="Téléphone"
                            hide-details
                            data-cy="ThirdCompanyUsersEdition-phone"
                            density="compact"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </v-window-item>
    </v-window>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";

import UPSERT_THIRD_COMPANY_DATA from "../graphql/company/UpsertThirdPartyCompany.gql";
import THIRD_COMPANY_DATA from "../graphql/invoice/ThirdPartyCompanyData.gql";

import { emailPattern } from "../services/emailServices";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

@Component({
    components: {},
    emits: [
        "step",
        "valid",
        "create",
        "createUser",
        "client",
        "edit-mode",
        "emit-upsert",
        "create-user",
    ],
})
export class ThirdCompanyUsersEdition extends Vue {
    @Prop({ required: true }) companyId!: string;
    @Prop({ required: true }) inThirdPartyCompanyId!: string;
    @Prop({ default: false }) onlyToCreate: boolean;
    @Prop() value: boolean;
    @Prop() actions: any;
    @Prop({ default: true }) showEditUsers: boolean;

    step = "default";

    search = "";

    result = [];

    selectedClient = { email: "" };

    newUserValid = false;

    previousUser = {
        firstname: "",
        lastname: "",
        email: "",
        phoneNumber: "",
    };

    newUser = {
        firstname: "",
        lastname: "",
        email: "",
        phoneNumber: "",
    };

    userExists = false;

    userDelete = false;

    users = [];

    loading = false;

    thirdPartyCompanyId_: string = "";

    get thirdPartyCompanyId() {
        if (this.thirdPartyCompanyId_) {
            return this.thirdPartyCompanyId_;
        }
        return this.inThirdPartyCompanyId;
    }

    set thirdPartyCompanyId(value) {
        this.thirdPartyCompanyId_ = value;
    }

    beforeMount() {
        if (this.onlyToCreate) {
            this.step = "createUser";
        }
        this.$emit("step", this.step);
        if (this.actions && this.actions.action) {
            this.actions.action.function = this.valid;
            this.actions.action.previous = this.previous;
            this.actions.action.disabled = !this.isValid;
            // this.actions.action.dialog = this.dialog
            this.actions.action.title = () => {
                return this.previousUser.email ? "Modifier le contact" : "Ajouter un contact";
            };
            this.actions.action.display = () => {
                return this.step === "createUser";
            };
        }
    }

    previous() {
        this.previousUser = {
            firstname: "",
            lastname: "",
            email: "",
            phoneNumber: "",
        };
        this.step = "default";
    }

    @Apollo({
        query: THIRD_COMPANY_DATA,
        variables() {
            return {
                thirdPartyCompanyId: this.thirdPartyCompanyId,
            };
        },
        skip() {
            return !this.companyId || !this.thirdPartyCompanyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loading = loading;
            if (!loading) {
                //verif si défini sinon liste vide
                if (data?.company?.thirdPartyCompanyData) {
                    const thirdPartyCompanyData = data.company.thirdPartyCompanyData;
                    if (thirdPartyCompanyData.users) {
                        this.users = thirdPartyCompanyData.users;
                    } else {
                        thirdPartyCompanyData.users = [];
                        this.users = [];
                        this.newUser = {
                            firstname: "",
                            lastname: "",
                            email: "",
                            phoneNumber: "",
                        };
                    }
                    this.thirdPartyCompanyData = thirdPartyCompanyData;
                } else {
                    this.thirdPartyCompanyData = { users: [] };
                    this.users = [];
                    this.newUser = {
                        firstname: "",
                        lastname: "",
                        email: "",
                        phoneNumber: "",
                    };
                }
            }
        },
    })
    thirdPartyCompanyData: any = {
        users: [],
    };

    async valid() {
        switch (this.step) {
            case "default": {
                this.$emit("valid", this.selectedClient);
                break;
            }
            case "createUser": {
                this.selectedClient = await this.createClient();
                // this.$emit('create', this.selectedClient);
                if (this.onlyToCreate) {
                    // this.$emit('step' , this.step)
                    this.$emit("createUser", this.selectedClient);
                }
                this.selectClient(this.selectedClient);
                this.step = "default";
                break;
            }
        }
    }

    @Watch("isValid")
    changeDisabled() {
        this.actions.action.disabled = !this.isValid;
    }

    clearUserTextField() {
        this.newUser = {
            firstname: "",
            lastname: "",
            email: "",
            phoneNumber: "",
        };
    }

    get isValid() {
        switch (this.step) {
            case "default":
                return this.selectedClient.email;
            case "createUser":
                this.$emit("create-user", this.newUserValid);
                return this.newUserValid;
        }
        return false;
    }

    get thirdPartyCompanyUsers() {
        if (this.thirdPartyCompanyData?.users) {
            if (this.search) {
                return this.thirdPartyCompanyData.users.filter(
                    (client) =>
                        client.firstname.toLowerCase().includes(this.search.toLowerCase()) ||
                        client.lastname.toLowerCase().includes(this.search.toLowerCase()) ||
                        client.email.toLowerCase().includes(this.search.toLowerCase()),
                );
            }
            return this.thirdPartyCompanyData.users;
        }
        return [];
    }

    getClientClass(clientEmail) {
        let clientClass = "mt-2 oss-list-item";
        if (
            this.selectedClient &&
            this.selectedClient.email &&
            clientEmail === this.selectedClient.email
        ) {
            clientClass += " oss-list-item-selected";
        }
        return clientClass;
    }

    selectClient(client) {
        if (!this.showEditUsers) {
            this.selectedClient = { ...this.selectedClient, ...client };
            this.$emit("client", client);
        }
    }

    emitStepFromComponent() {
        this.$emit("step", this.step);
    }

    emitEditMode() {
        this.$emit("edit-mode", true);
    }

    editUser(user) {
        this.step = "createUser";
        this.emitStepFromComponent;
        this.emitEditMode;
        this.newUser = user;
        this.previousUser = user;
        this.userExists = true;
    }

    async deleteUser(userToDelete) {
        if (userToDelete) {
            const indexUserToDelete = this.thirdPartyCompanyData.users.findIndex(
                (user) => user.email === userToDelete.email,
            );
            this.thirdPartyCompanyData.users.splice(indexUserToDelete, 1);

            if (this.thirdPartyCompanyId && this.companyId) {
                await this.$apollo.mutate({
                    mutation: UPSERT_THIRD_COMPANY_DATA,
                    variables: {
                        parentCompanyId: this.companyId,
                        thirdPartyCompanyId: this.thirdPartyCompanyId,
                        data: this.thirdPartyCompanyData,
                    },
                });
            }
        }
    }

    changeStep() {
        this.previousUser = {
            firstname: "",
            lastname: "",
            email: "",
            phoneNumber: "",
        };
        this.step = "default";
        this.$emit("step", this.step);
    }

    get companyClientId() {
        return this.thirdPartyCompanyData.id || "";
    }

    async createClient() {
        const userCreate = JSON.parse(JSON.stringify(this.newUser));
        // debugger
        if (this.thirdPartyCompanyData.users) {
            if (!this.userExists) {
                this.thirdPartyCompanyData.users.push(userCreate);
            } else {
                const indexReplaceUser = this.thirdPartyCompanyData.users.findIndex(
                    (user) => user.mail === this.previousUser.email,
                );
                this.thirdPartyCompanyData.users[indexReplaceUser] = userCreate;
            }
        } else {
            this.thirdPartyCompanyData.users = [userCreate];
        }

        const client = await this.$apollo.mutate({
            mutation: UPSERT_THIRD_COMPANY_DATA,
            variables: {
                parentCompanyId: this.companyId,
                thirdPartyCompanyId: this.thirdPartyCompanyId,
                data: this.thirdPartyCompanyData,
            },
        });

        if (client.data && client.data.upsertThirdPartyCompany) {
            this.thirdPartyCompanyId = client.data.upsertThirdPartyCompany.id;
            this.$emit("emit-upsert", true);
        }

        const registeredClient = { ...this.newUser };

        this.step = "default";

        this.newUserValid = false;
        this.$emit("create-user", this.newUserValid);

        this.newUser = {
            firstname: "",
            lastname: "",
            email: "",
            phoneNumber: "",
        };

        // this.$apollo.queries.thirdPartyCompanyDatas.refetch()

        return { ...this.selectedClient, ...registeredClient };
    }

    get rules() {
        return {
            isEmail: (value) => {
                if (!value) return this.$t("settings.itemRequired");
                const test =
                    emailPattern.test(value) || value === "" || value == null ? true : false;
                return test || "E-mail invalide";
            },
            // emailAlreadyExists: (value) => {
            //     for(const email of this.thirdPartyCompanyUsers.email){
            //         if(value === email){
            //             return "L'e-mail existe déjà"
            //         }
            //     }
            //     return true
            // }
            // notEmpty: value => {
            //         return !!value || 'A remplir'
            // }
        };
    }
}

export default toNative(ThirdCompanyUsersEdition);
</script>
